'use client'

import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion'
import { BookOpen, ClipboardCheck, FileCheck2, Handshake, PlaneTakeoff, School, ArrowRight } from 'lucide-react'
import { FlagJP, FlagPK } from '@/components/site/Flags'
import { ApplyButton } from '@/components/apply/ApplyButton'

type Locale = 'en' | 'ja'

const STEPS = [
  { icon: School, en: ['Enrol at NCP', 'Pick a course and a morning or afternoon batch for the 2026/27 intake, in Lahore, Alipur Chatha or online.'], ja: ['NCPに入学', '2026/27年度のコースと午前・午後クラスを選択。ラホール、アリプル・チャタ、またはオンライン。'] },
  { icon: BookOpen, en: ['Learn Japanese', 'Small classes, five days a week: from hiragana to the N4 / A2 level most employers ask for.'], ja: ['日本語を学ぶ', '週5日の少人数クラス。ひらがなから、多くの企業が求めるN4・A2レベルまで。'] },
  { icon: ClipboardCheck, en: ['Pass your tests', 'JLPT or JFT-Basic for Japanese, plus the skills test for your SSW field.'], ja: ['試験に合格', '日本語はJLPTまたはJFT-Basic、さらに特定技能の分野別技能試験。'] },
  { icon: Handshake, en: ['Interview & matching', 'Japanese CV, mock interviews with Japanese teachers, and guidance to employers.'], ja: ['面接・マッチング', '日本語の履歴書、日本人講師との模擬面接、企業とのマッチング支援。'] },
  { icon: FileCheck2, en: ['Visa & departure', 'Help with the visa paperwork and pre-departure training on life and work in Japan.'], ja: ['ビザ・出国準備', 'ビザ書類のサポートと、日本での生活・仕事についての出国前研修。'] },
  { icon: PlaneTakeoff, en: ['Land in Japan', 'Start work with the language, manners and confidence to succeed.'], ja: ['日本に到着', '成功に必要な日本語・マナー・自信を持って、仕事をスタート。'] },
]

const T = {
  en: { eyebrow: 'Your route', title: 'Lahore to Tokyo in six stops', lead: 'A clear path from your first Japanese class to your first day at work in Japan.', from: 'Lahore', to: 'Tokyo', cta: 'Start at stop 1' },
  ja: { eyebrow: 'あなたのルート', title: 'ラホールから東京へ、6つのステップ', lead: '最初の日本語の授業から、日本での初出勤まで。わかりやすい道のりです。', from: 'ラホール', to: '東京', cta: 'ステップ1から始める' },
}

export default function JourneySection({ locale = 'en' }: { locale?: Locale }) {
  const t = T[locale]
  const ref = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 75%', 'end 55%'] })
  const p = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.4 })
  const pct = useTransform(p, (v) => `${(reduced ? 1 : v) * 100}%`)
  const lineW = useTransform(p, (v) => `calc(${(reduced ? 1 : v) * 83.34}%)`)
  const planeX = useTransform(p, (v) => `calc(8.33% + ${(reduced ? 1 : v) * 83.34}%)`)

  return (
    <section className="relative py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="section-eyebrow section-eyebrow-centered">{t.eyebrow}</p>
          <h2 className="mt-4 text-3xl font-bold tracking-[-0.03em] text-brand-navy sm:text-4xl md:text-5xl">{t.title}</h2>
          <p className="mt-4 text-base leading-relaxed text-brand-charcoal/75 sm:text-lg">{t.lead}</p>
        </div>

        <div ref={ref} className="relative mt-14">
          {/* desktop route line */}
          <div className="relative mx-auto mb-8 hidden h-12 lg:block" aria-hidden data-no-reveal>
            <div className="absolute left-[8.33%] right-[8.33%] top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-[repeating-linear-gradient(90deg,#d6d9dd_0_10px,transparent_10px_18px)]" />
            <motion.div className="absolute left-[8.33%] top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-gradient-to-r from-brand-green via-brand-gold to-brand-red" style={{ width: lineW }} />
            <motion.div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: planeX }}>
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-brand-red shadow-[0_10px_25px_-8px_rgba(187,20,58,0.6)] ring-2 ring-brand-red/20">
                <PlaneTakeoff className="h-5 w-5" />
              </span>
            </motion.div>
            <span className="absolute -top-1 left-0 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-green">
              <FlagPK /> {t.from}
            </span>
            <span className="absolute -top-1 right-0 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-red">
              {t.to} <FlagJP />
            </span>
          </div>

          {/* mobile route line */}
          <div className="absolute bottom-6 left-[27px] top-2 w-[3px] rounded-full bg-[repeating-linear-gradient(180deg,#d6d9dd_0_10px,transparent_10px_18px)] lg:hidden" aria-hidden>
            <motion.div className="w-full rounded-full bg-gradient-to-b from-brand-green via-brand-gold to-brand-red" style={{ height: pct }} />
          </div>

          <ol className="relative grid gap-4 lg:grid-cols-6 lg:gap-3">
            {STEPS.map((s, i) => {
              const [title, body] = s[locale]
              const Icon = s.icon
              return (
                <li key={title} className="relative flex gap-4 lg:block">
                  <span className="relative z-[1] flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white text-brand-green shadow-[0_12px_28px_-14px_rgba(14,75,59,0.45)] ring-1 ring-brand-cool-gray lg:mx-auto lg:mb-4">
                    <Icon className="h-6 w-6" />
                    <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-brand-red text-[11px] font-bold text-white">{i + 1}</span>
                  </span>
                  <div className="flex-1 rounded-2xl border border-brand-cool-gray/80 bg-white/85 p-4 backdrop-blur lg:min-h-[176px] lg:text-center">
                    <h3 className="text-base font-bold text-brand-navy">{title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-brand-charcoal/75">{body}</p>
                  </div>
                </li>
              )
            })}
          </ol>
        </div>

        <div className="mt-10 text-center">
          <ApplyButton className="btn-primary">
            {t.cta} <ArrowRight className="ml-2 h-4 w-4" />
          </ApplyButton>
        </div>
      </div>
    </section>
  )
}
